import React, { useState } from 'react';
import { UserCheck, X } from 'lucide-react';
import { useSubstituirContato } from '../hooks';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { Contato } from '../api';

interface Props {
  clientId: string;
  oldContact: Contato;
  candidates: Contato[];
  onClose: () => void;
  onDone?: () => void;
}

/**
 * Modal de substituição definitiva de contato (SPEC 03 Fase B —
 * Exemplo 3). Restrito por `client:manage-contact-substitution` no
 * backend; qualquer perfil abaixo recebe 403 no submit.
 *
 * O usuário seleciona um contato existente e ativo como sucessor,
 * informa motivo (obrigatório) e confirma. O backend transfere
 * responsabilidades, marca o antigo como "substituido" e devolve o
 * mapa de transferências para feedback.
 */
export const SubstituicaoContatoModal: React.FC<Props> = ({
  clientId,
  oldContact,
  candidates,
  onClose,
  onDone,
}) => {
  const substituir = useSubstituirContato(clientId);
  const [newContactId, setNewContactId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const podeSubmeter = !!newContactId && reason.trim().length >= 3;

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setError(null);
    if (!newContactId) {
      setError('Selecione o contato sucessor.');
      return;
    }
    if (reason.trim().length < 3) {
      setError('Motivo é obrigatório (mín. 3 caracteres).');
      return;
    }
    try {
      await substituir.mutateAsync({
        contactId: oldContact.id,
        newContactId,
        reason: reason.trim(),
      });
      onDone?.();
      onClose();
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Erro ao substituir contato.'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck size={20} /> Substituir contato
          </h3>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-6" data-testid="substituir-form">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-gray-500">Contato a substituir</div>
            <div className="font-medium text-gray-900">{oldContact.nome}</div>
            <div className="text-xs text-gray-500">{oldContact.email}</div>
          </div>

          <div>
            <AutocompleteSelect
              label="Sucessor (contato ativo) *"
              options={[
                { value: '', label: 'Selecione…' },
                ...candidates.map((c) => ({ value: c.id, label: `${c.nome} — ${c.email}` })),
              ]}
              value={newContactId}
              onChange={(v) => setNewContactId(v)}
            />
            {candidates.length === 0 && (
              <span className="mt-1 block text-xs text-yellow-700">
                Nenhum outro contato ativo disponível. Cadastre o sucessor antes.
              </span>
            )}
          </div>

          <div>
            <Textarea
              label="Motivo *"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              minLength={3}
              showCharCount
              data-testid="substituir-motivo"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Todas as responsabilidades ativas serão transferidas para o sucessor. O contato
              atual será marcado como "Substituído" mas permanecerá vinculado a documentos e
              contratos históricos.
            </span>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="substituir-error">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={substituir.isPending || !podeSubmeter}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="substituir-btn-confirmar"
            >
              {substituir.isPending ? 'Substituindo…' : 'Substituir'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
